import React, { useState, useEffect } from 'react';
import { X, PlusSquare, Download, Smartphone } from 'lucide-react';

const IosShareIcon = ({ size = 24, color = "currentColor", style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

const InstallPWA = ({ darkMode, user, isOpen, onClose, forceIOS }) => {
  const [promptInstall, setPromptInstall] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Prüfen, ob App bereits installiert ist (Standalone Mode)
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Event Listener für Android/Desktop Chrome
    const handler = (e) => {
      e.preventDefault();
      setPromptInstall(e);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check für iOS
    const isIosDevice = /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
    if (isIosDevice || forceIOS) {
      setIsIOS(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [forceIOS]);

  // Auto-Show Logik
  useEffect(() => {
    if (isInstalled) return;
    
    // Check if dismissed
    const isDismissed = localStorage.getItem('pwa_popup_dismissed');
    if (isDismissed) return;

    // Nur anzeigen, wenn User eingeloggt ist
    if (!user) return;

    // Show if we have an install prompt (Android/Desktop) or if it's iOS
    if (promptInstall || isIOS) {
        const timer = setTimeout(() => setShowPopup(true), 3000);
        return () => clearTimeout(timer);
    }
  }, [user, promptInstall, isIOS, isInstalled]);

  const handleInstall = (e) => {
    e.preventDefault();
    if (!promptInstall) {
      return;
    }
    promptInstall.prompt();
    promptInstall.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
            handleClose();
        }
        setPromptInstall(null);
    });
  };

  const handleClose = () => {
      setShowPopup(false);
      if (onClose) onClose();
      localStorage.setItem('pwa_popup_dismissed', 'true');
  };

  // Anzeigen wenn: (Auto-Show ist true ODER Manuell geöffnet) UND (Nicht installiert ODER Manuell geöffnet)
  const shouldShow = (showPopup || isOpen) && (!isInstalled || isOpen);

  if (!shouldShow) return null;

  return (
    <div className="modal-overlay" style={{ zIndex: 3000 }}>
      <div className="modal-content" style={{ 
        maxWidth: '420px', 
        textAlign: 'center', 
        position: 'relative', 
        backgroundColor: darkMode ? '#1e1e1e' : '#fff', 
        color: darkMode ? '#fff' : '#333',
        backdropFilter: 'blur(15px)',
        WebkitBackdropFilter: 'blur(15px)',
        border: darkMode ? '1px solid #333' : '1px solid rgba(255, 255, 255, 0.5)',
        padding: '40px',
        borderRadius: '24px'
      }}>
        <button 
            onClick={() => { if (navigator.vibrate) navigator.vibrate(10); handleClose(); }} 
            style={{ position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', cursor: 'pointer', color: darkMode ? '#888' : '#ccc' }}
        >
            <X size={24} />
        </button>
        
        <div style={{ backgroundColor: darkMode ? 'rgba(28, 176, 246, 0.2)' : '#ddf4ff', borderRadius: '24px', padding: '20px', display: 'inline-flex', marginBottom: '20px' }}>
            <Smartphone size={48} color="#1cb0f6" strokeWidth={3} />
        </div>

        <h2 style={{ marginBottom: '10px', color: '#1cb0f6', fontSize: '1.5rem' }}>App installieren</h2>
        
        {isIOS || !promptInstall ? (
            <div style={{ textAlign: 'left', fontSize: '0.95rem', lineHeight: '1.5', color: darkMode ? '#ccc' : '#555' }}>
                <p style={{ marginBottom: '15px', textAlign: 'center' }}>So installierst du die App auf deinem iPhone/iPad:</p>
                <ol style={{ paddingLeft: '20px', margin: '0' }}>
                    <li style={{ marginBottom: '12px' }}>Tippe unten in der Leiste auf das <strong>Teilen</strong>-Symbol <IosShareIcon size={20} color={darkMode ? '#fff' : '#1CB0F6'} style={{ verticalAlign: 'text-bottom', margin: '0 2px' }} />.</li>
                    <li>Scrolle etwas nach unten und wähle <strong>"Zum Home-Bildschirm"</strong> <PlusSquare size={20} color={darkMode ? '#fff' : '#1CB0F6'} style={{ verticalAlign: 'text-bottom', margin: '0 2px' }} />.</li>
                </ol>
            </div>
        ) : (
            <div style={{ color: darkMode ? '#ccc' : '#555' }}>
                <p style={{ marginBottom: '20px' }}>Lade die App herunter, um offline zu lernen und schnelleren Zugriff zu haben!</p>
                <button 
                  onClick={(e) => { if (navigator.vibrate) navigator.vibrate(10); handleInstall(e); }} 
                  style={{ 
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                    width: '100%',
                    backgroundColor: darkMode ? '#2b2b2b' : '#ffffff',
                    color: '#1cb0f6',
                    border: darkMode ? '2px solid #444' : '2px solid #e5e5e5',
                    borderBottom: darkMode ? '4px solid #444' : '4px solid #e5e5e5',
                    padding: '15px',
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: '900',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    transition: 'all 0.1s',
                    marginTop: '20px'
                  }}
                  onMouseDown={e => e.currentTarget.style.borderBottomWidth = '2px'}
                  onMouseUp={e => e.currentTarget.style.borderBottomWidth = '4px'}
                >
                    <Download size={20} />
                    Jetzt installieren
                </button>
            </div>
        )}
      </div>
    </div>
  );
};

export default InstallPWA;
